import {
  DermloopClinicalActionTypeEnum,
  DermloopClinicalActionVariantEnum,
  StatusEnum,
} from '@dermloop/static-values';
import {
  IsArray,
  IsBoolean,
  IsEnum,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateNested,
  ValidateBy,
  max,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { DiagnosisGroupDto } from '../diagnoses/diagnosis-group.dto';
import { DiagnosisDto } from '../diagnoses/diagnosis.dto';
import { ModelViewerLocation } from '../model-viewer-location.dto';
import type { ConditionHealthRecordDto } from '../patient/health-record.dtos';
import { LesionIndexDataDto } from '../search';
import {
  ConditionImageDto,
  ConditionImageUrlsDto,
} from './condition-image.dtos';
import { Type } from 'class-transformer';

export enum SearchConditionDtoUser {
  All = 'All',
  Own = 'Own',
  MedicalUnit = 'MedicalUnit',
}

export enum SearchConditionDtoAction {
  Dismiss = 'dissmiss',
  TeledermatologicalReferral = 'tele',
  Biopsy = 'biopsy',
  FollowUp = 'follow-up',
}

export class ActionDto {
  @IsEnum(DermloopClinicalActionTypeEnum)
  type: DermloopClinicalActionTypeEnum;

  @IsOptional()
  @IsEnum(DermloopClinicalActionVariantEnum)
  variant?: DermloopClinicalActionVariantEnum | null;

  @IsString()
  id: string;
}

export const HEALTH_RECORD_CONDITIONS_SEARCH_TAKE = 1000;

export class SearchConditionDto {
  // Bounds guard the page directly against LIMIT/OFFSET in the PostgreSQL condition search: a
  // negative take/skip is a Postgres error (500) and an unbounded skip an expensive offset walk,
  // so reject them at the DTO instead. /conditions/search queries Postgres directly, so these
  // bounds are enforced here at the API boundary rather than downstream. One patient's list may
  // take more, so a case past the first page still resolves.
  @IsOptional()
  @IsNumber()
  @Min(1)
  @ValidateBy({
    name: 'max',
    validator: {
      validate: (value, args) =>
        max(
          value,
          (args?.object as SearchConditionDto).healthRecordId
            ? HEALTH_RECORD_CONDITIONS_SEARCH_TAKE
            : 50,
        ),
      defaultMessage: () => 'take exceeds the condition search limit',
    },
  })
  take?: number;

  @IsOptional()
  @IsIn([SearchConditionDtoUser.MedicalUnit, SearchConditionDtoUser.Own])
  users?: SearchConditionDtoUser;

  @IsOptional()
  @IsEnum(StatusEnum)
  status?: StatusEnum;

  @IsOptional()
  @IsUUID()
  conditionTypeId?: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionTypeId */
  skinConditionTypeId?: string;

  @IsOptional()
  @IsBoolean()
  isPatientContacted?: boolean;

  @IsOptional()
  @IsBoolean()
  correct?: boolean;

  // This is used in cases-list for final diagnosis and in tele-list for tele diagnosis
  @IsOptional()
  @IsUUID('all', { each: true })
  accurateDiagnosisIds?: string[];

  // This is used in cases-list for tentative diagnosis and in tele-list for clinical diagnosis
  @IsOptional()
  @IsUUID('all', { each: true })
  clinicalDiagnosisIds?: string[];

  @IsOptional()
  @Type(() => ActionDto)
  @ValidateNested({ each: true })
  actions?: ActionDto[];

  // SSN query
  @IsOptional()
  @IsString()
  query?: string;

  @IsOptional()
  @IsUUID('all')
  healthRecordId?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(1000)
  skip?: number;

  // If true, includes clinical image urls in response
  @IsOptional()
  @IsBoolean()
  includeImageUrls?: boolean;
}

export class LatestConditionInfoDto implements BaseDto {
  id: string;
  created?: string;
  updated?: string;
  createdByUserId?: string;

  @IsOptional()
  @IsString()
  latestTriageCreatedByUserId?: string;

  latestClinicalImage: ConditionImageDto;

  @IsBoolean()
  complete: boolean;

  @IsOptional()
  @IsNotEmpty()
  preciseLocation?: ModelViewerLocation;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  healthRecordId: string;

  @IsString()
  latestTriageId: string;

  @IsString()
  latestAction: string;

  @IsString()
  latestActionDetails: string;

  @IsNotEmpty()
  @IsString()
  conditionLatestDiagnosisName: string;

  /** @deprecated Use conditionLatestDiagnosisName */
  @IsNotEmpty()
  @IsString()
  lesionLatestDiagnosisName: string;

  @IsOptional()
  @IsBoolean()
  isTriageDiagnosed?: boolean;

  @IsOptional()
  @IsBoolean()
  canBeDeleted?: boolean;

  @IsOptional()
  @IsNotEmpty()
  @IsNumber()
  creationOrder?: number;
}
export type AssessmentType = 'pathology' | 'tele' | 'clinical';
export interface AssessmentDto {
  type: AssessmentType;
  action?: DermloopClinicalActionTypeEnum;
  actionDetails?: string;
  diagnosis?: DiagnosisDto;
  diagnosisGroup?: DiagnosisGroupDto;
  differentialDiagnosis?: DiagnosisDto;
  differentialDiagnosisGroup?: DiagnosisGroupDto;
  secondaryDiagnosis?: DiagnosisDto;
  secondaryDiagnosisGroup?: DiagnosisGroupDto;
  createdDate: string;
  isComplete?: boolean;
}

export class ConditionSearchResultDto {
  @IsString()
  @IsNotEmpty()
  conditionId: string;

  @IsOptional()
  @IsString()
  /** @deprecated Use conditionId */
  lesionId?: string;

  @IsUUID()
  @IsNotEmpty()
  conditionTypeId: string;

  @IsOptional()
  @IsUUID()
  /** @deprecated Use conditionTypeId */
  skinConditionTypeId?: string;

  @IsNotEmpty()
  healthRecord: ConditionHealthRecordDto;

  @IsOptional()
  @IsNotEmpty()
  preciseLocation?: ModelViewerLocation;

  @IsOptional()
  latestAssessment?: AssessmentDto;

  @IsOptional()
  latestUserAssessment?: AssessmentDto;

  @IsOptional()
  @IsBoolean()
  latestUserAssessmentCorrect?: boolean;

  @IsOptional()
  pendingAssessment?: AssessmentType[];

  @IsOptional()
  latestDermoscopicImage?: ConditionImageDto;

  @IsOptional()
  latestClinicalImage?: ConditionImageDto;

  @IsOptional()
  userLatestDermoscopicImage?: ConditionImageDto;

  @IsOptional()
  userLatestClinicalImage?: ConditionImageDto;
}

/**
 * DTO for condition search results. Includes all data of condition index data dto,
 * and optional urls.
 */
export interface ConditionSearch2ResultDto extends LesionIndexDataDto {
  urls?: ConditionImageUrlsDto;
  alternativeUrls?: ConditionImageUrlsDto;
}
